import type { BasisView, VersionLabel } from '@tabel/shared/schemas/rules';
import { ruDate } from '../../lib/format';

/*
 * Тексты страницы «Правила расчёта» и документа для печати (004, US1; contracts/ui.md; FR-058). Язык — ASD-STE100:
 * короткие предложения, условие первым, нейтральный род, одно слово — одно значение; без кодов правил и латиницы.
 * Проверяет `texts.test.ts` (`@tabel/shared/plain-language`). Тексты правил — вопросы, ответы, подписи полей,
 * термины — приходят с сервера: их проверяют тесты движка.
 */

/** Предложения страницы. */
export const T = {
  intro:
    'На этой странице — правила, по которым система считает часы в табеле. Каждое правило — ответ на вопрос о расчёте дня.',
  lawText: 'Эти правила задаёт закон. Изменить их нельзя.',
  fixedText: 'Это правило задаёт система.',
  noDays: 'На объекте пока нет таких дней.',
  exampleError: 'Пример не загрузился.',
  answerError: 'Ответ правила не удалось показать.',
  sectionEmpty: 'Объект пока не применяет правила этого раздела.',
  periodTwo: 'В периоде табеля действуют разные версии правил.',
  historyEmpty: 'Версий правил пока нет.',
  notConfigured: 'Для этого объекта правила расчёта ещё не настроены.',
  // 007, поправка 20.10.2026 (FR-004a, FR-004b): площадка до запуска. Пустое состояние — одна фраза (дизайн-система,
  // EmptyState); правила к запуску — подсказка о состоянии экрана (Callout).
  notPrepared: 'Правила появятся, когда администратор системы подготовит их в шаге 10 подключения площадки.',
  launchNoDays: 'Дни площадки система посчитает после запуска: у площадки ещё нет табелей.',
  dayError: 'Расчёт дня не загрузился.',
  recalcRunning: 'Система пересчитывает табели…',
  searchPlaceholder: 'Например, обед',
  // US2: изменение правила, пример, влияние, черновик
  savedToDraft: 'Сохранено в черновик. В табели попадёт после «Опубликовать».',
  /** 007, FR-004a: черновик правил к запуску публикует запуск площадки. */
  savedToLaunchDraft: 'Сохранено в черновик. Правила начнут действовать при запуске площадки.',
  noChange: 'Значения не изменены.',
  conflict:
    'Черновик изменился, пока это изменение было открыто. Ваши значения остались. Проверьте их и сохраните снова.',
  editHint: 'Измените значение. Здесь будет видно, сколько дней изменится.',
  impactError: 'Влияние не посчитано.',
  /** У значения ошибка проверки: дни с таким набором не считаются — «N дней с ошибкой расчёта» пугает зря. */
  impactBlocked: 'Влияние не посчитано, пока есть ошибка в значениях.',
  /** FR-024: черновик с ошибками сохраняется, опубликовать его нельзя (решение пользователя 16.10.2026). */
  saveWithErrors: 'С ошибкой черновик сохранится, но опубликовать его нельзя.',
  afterBlocked: 'Не посчитано, пока есть ошибка в значениях.',
  /** Поле или вариант, которые расчёт пока не читает (решение пользователя 16.10.2026). */
  notInCalc: 'Расчёт пока это не учитывает.',
  impactNone: 'часы не изменятся ни в одном дне',
  impactSigned: 'подписанные табели не меняются',
  noDayChanges: 'Ни один день не изменится.',
  dayUnchanged: 'Этот день не изменится.',
  exampleCalcError: 'Пример не посчитан.',
  notNumber: 'Введите число.',
  notWhole: 'Введите целое число.',
  wholeSeconds: 'Введите минуты так, чтобы получились целые секунды, например 15 или 0,5.',
  timeFormat: 'Введите время как 12:30.',
  required: 'Заполните это поле.',
  teamValue: 'Это значение меняет команда разработки.',
  draftNoLeader: 'Черновик без ведущего.',
  draftTaken: 'Черновик теперь ведёте вы.',
  transferText: 'Черновик будет вести выбранный администратор правил. Вы сможете только смотреть черновик.',
  noAdmins: 'Других администраторов правил на объекте нет.',
  personHint: 'Введите две буквы или больше.',
  noPeople: 'Людей с такими буквами нет.',
  peopleError: 'Список людей не загрузился.',
  editedDaysTitle: 'Дни с правкой: расчёт изменится, в ячейке останется правка.',
  noEditedDays: 'Таких дней нет.',
  previewError: 'Влияние черновика не посчитано.',
  changesEmpty: 'В черновике нет изменений.',
  questionPlus: 'вопрос появится',
  questionMinus: 'вопрос исчезнет',
  // US3: окно «Опубликовать изменения» (FR-033–FR-037, FR-039)
  publishIntro: 'Табели пересчитаются по новым правилам. Подписанные табели не меняются.',
  goldenOk: 'Так и должно быть по новому правилу',
  seen: 'Список дней, в которых изменятся часы, просмотрен.',
  selfApproved: 'Вы и автор, и тот, кто публикует, — это запишется в историю.',
  decidedHint: 'Этот текст увидят в истории изменений и в строке «Почему так» у правила.',
  decidedPlaceholder: 'Например: обед не короче 40 мин, решение главного инженера от 15.10.2026.',
  docPlaceholder: '№ 45 от 15.10.2026',
  questionHint: 'Номер вопроса в реестре расчёта. По номеру видно, откуда пришло правило.',
  previewStale:
    'Данные изменились, влияние посчитано заново. Проверьте числа и нажмите «Опубликовать» снова.',
  futureNoDays: 'С этой даты дней ещё нет. Правила начнут действовать с этой даты.',
  whyErrors: 'В черновике есть ошибки. Исправьте их.',
  whyComputing: 'Влияние ещё считается.',
  whyImpact: 'Влияние не посчитано. Нажмите «Повторить».',
  whyKind: 'Выберите, почему меняем.',
  whyDecided: 'Напишите, что решили.',
  whyDoc: 'Укажите номер и дату документа.',
  whyGoldens: 'Поставьте флажок «Так и должно быть» у каждого проверочного примера.',
  whyRisks: 'Поставьте флажок «Понимаю риск» у каждого предупреждения.',
  whySeen: 'Поставьте флажок «Список дней, в которых изменятся часы, просмотрен».',
  publishedRecalc: 'Опубликовано. Система пересчитывает табели.',
  closeAskText: 'Введённое в окне не сохранится.',
  // US5: проверочные примеры (FR-051–FR-053)
  goldensIntro: 'Каждый черновик и возврат система проверяет на этих днях.',
  goldensEmpty: 'Проверочных примеров пока нет.',
  goldensEmptyHint:
    'Чтобы добавить проверочный пример, откройте меню «…» у примера дня правила и выберите «Сделать проверочным примером».',
  goldenSame: 'совпадает',
  goldenConfirmed: 'подтверждено: «Так и должно быть»',
  goldenWillChangeDetails: 'изменятся пометки или другие подробности дня',
  pinIntro: 'Система будет проверять этот день при каждом изменении правил.',
  pinExpected: 'Это расчёт дня по правилам, которые действуют на эту дату.',
  pinPseudonym: 'В проверочном примере вместо ФИО будет псевдоним.',
  pinHint: 'Напишите, что проверяет этот день. Не пишите ФИО и табельные номера.',
  pinPlaceholder: 'Например: длинная смена с выходом на обед',
  titlePersonal: 'В названии не должно быть ФИО и номеров людей.',
  whyTitle: 'Напишите название: от 3 до 120 знаков.',
  pinned: 'Проверочный пример сохранён. Система проверяет его при каждом изменении правил.',
  // US6: каталог и просьбы о правилах (FR-054–FR-057)
  catalogIntro: 'Эти правила система умеет считать. Объект их пока не применяет.',
  catalogEmpty: 'Объект применяет все правила, которые система умеет считать.',
  needSchedules: 'Сначала добавьте графики работы.',
  needCalendar: 'Сначала загрузите производственный календарь.',
  needDocuments: 'Сначала введите документы: отпуска и больничные.',
  needMarks: 'Сначала включите отметки бригадира.',
  needOther: 'Сначала нужны данные, которых у объекта нет.',
  describeHint:
    'Не пишите ФИО и табельные номера. Команда разработки заведёт по просьбе вопрос в реестре расчёта.',
  describePlaceholder:
    'Например: если бригада работает в выходной по распоряжению, ставить часы строкой «РВ».',
  describePersonal: 'В просьбе есть ФИО или номер. Команда разработки увидит этот текст.',
  describeAgain: 'Чтобы отправить текст как есть, нажмите «Отправить команде разработки» ещё раз.',
  whyDescribe: 'Опишите правило.',
  sent: 'Отправлено команде разработки. Номер вопроса появится, когда его заведут в реестре расчёта.',
  requestsIntro: 'Номер в реестре ставит команда разработки, когда заводит вопрос в реестре расчёта.',
  requestsEmpty: 'Просьб о правилах пока нет.',
  statusSaved: 'Статус просьбы сохранён.',
  codeHint: 'Номер вопроса из реестра расчёта. Этот номер можно сразу выбрать при публикации.',
  whyCode: 'Введите номер в реестре, например Q-30.',
  whyRejectReason: 'Напишите причину.',
} as const;

/** Подписи кнопок, вкладок, полей, заголовков и областей — до 6 слов. */
export const LABEL = {
  /** Кнопка «?» у поля (подпись для чтения с экрана). */
  hint: 'Подсказка',
  title: 'Правила расчёта',
  howTitle: 'Как система считает день',
  /** Свёрнутые поля под ответом в карточке правила. */
  ruleDetails: 'Подробнее о правиле',
  termsTitle: 'Термины',
  termsCalc: 'Слова расчёта',
  termsEditor: 'Слова редактора правил',
  views: 'Виды',
  tabRules: 'Правила',
  tabHistory: 'История изменений',
  date: 'Правила на дату',
  today: 'Сегодня',
  search: 'Найти правило',
  searchClear: 'Очистить поиск',
  found: 'Найденные правила',
  actions: 'Действия с правилами',
  printRules: 'Скачать правила на дату',
  downloadHistory: 'Скачать историю изменений',
  back: 'Вернуться к табелю',
  example: 'Пример дня',
  examplePrefix: 'Пример:',
  golden: 'Проверочный пример',
  openDay: 'Открыть расчёт дня',
  dayPanel: 'Расчёт дня',
  howCounted: 'Как посчитано',
  close: 'Закрыть',
  retry: 'Повторить',
  loading: 'Загрузка…',
  loadError: 'Правила не загрузились',
  notPreparedTitle: 'Правил пока нет',
  launchTitle: 'Правила к запуску.',
  notOnDateTitle: 'Правил на эту дату нет',
  openSite: 'Открыть подключение площадки',
  openCurrent: 'Открыть действующие правила',
  historyError: 'История не загрузилась',
  periodVersions: 'Версия правил в периоде',
  related: 'Связано:',
  dependsOn: 'зависит от',
  usedBy: 'влияет на',
  versions: 'Версии правил',
  print: 'Напечатать',
  printTitle: 'Документ для печати',
  // US2: изменение правила, пример, влияние, черновик
  edit: 'Изменить',
  saveDraft: 'Сохранить в черновик',
  inDraft: 'В черновике',
  wasNow: 'Сейчас действует:',
  tabChanges: 'Что изменится',
  draftBar: 'Черновик правил',
  draftMore: 'Ещё действия с черновиком',
  publish: 'Опубликовать',
  transfer: 'Передать черновик',
  transferDo: 'Передать',
  take: 'Взять черновик',
  admin: 'Администратор правил',
  reason: 'Причина',
  cancel: 'Отмена',
  otherDay: 'Другой день, который изменится',
  pickDay: 'Выбрать день',
  showDay: 'Показать день',
  person: 'Человек',
  people: 'Люди',
  dayDate: 'Дата дня',
  now: 'Сейчас',
  after: 'После публикации',
  impactThis: 'Это изменение',
  impactWhole: 'Весь черновик',
  whichDays: 'В каких днях',
  daysTitle: 'В каких днях изменятся часы',
  computing: 'Система считает…',
  changesList: 'Изменения в черновике',
  changedDays: 'Дни, в которых изменятся часы',
  warnings: 'Предупреждения',
  riskExample: 'Пример:',
  editedMark: 'правка',
  lawLock: 'закон',
  // US3: публикация
  publishTitle: 'Опубликовать изменения',
  whatChanges: 'Что меняется',
  validFrom: 'Действует с',
  basisKind: 'Почему меняем',
  decided: 'Что решили',
  doc: 'Номер и дата документа',
  question: 'Вопрос реестра (если есть)',
  questionNone: 'Нет',
  goldensChanged: 'Проверочные примеры, у которых изменится результат',
  ackRisk: 'Понимаю риск',
  closeAsk: 'Закрыть окно публикации?',
  backToDialog: 'Вернуться к окну',
  closeDialog: 'Закрыть окно',
  newVersions: 'Новые версии',
  // US4–US6: возврат, проверочные примеры, каталог, просьбы
  closeAskAny: 'Закрыть окно?',
  goldens: 'Проверочные примеры',
  pin: 'Сделать проверочным примером',
  exampleActions: 'Действия с примером',
  savePin: 'Сохранить проверочный пример',
  goldenTitle: 'Название',
  expected: 'Ожидаемый результат',
  pinnedBy: 'Закреплён: кто и когда',
  byDraft: 'По черновику',
  goldensError: 'Проверочные примеры не загрузились',
  team: 'Команда разработки',
  addRule: 'Добавить правило',
  add: 'Добавить',
  catalogCan: 'Можно добавить',
  needSchedules: 'Сначала нужны графики работы',
  needCalendar: 'Сначала нужен производственный календарь',
  needDocuments: 'Сначала нужны документы',
  needMarks: 'Сначала нужны отметки бригадира',
  needOther: 'Сначала нужны другие данные',
  noRule: 'Нет нужного правила?',
  describe: 'Опишите правило словами',
  send: 'Отправить команде разработки',
  notApply: 'Не применять',
  tabRequests: 'Просьбы о правилах',
  requestsError: 'Просьбы не загрузились',
  waiting: 'Ждёт разбора',
  setRegistry: 'В реестре…',
  reject: 'Отклонить…',
  rejectDo: 'Отклонить',
  registryTitle: 'Номер в реестре расчёта',
  rejectTitle: 'Отклонить просьбу',
  questionCode: 'Номер в реестре',
  save: 'Сохранить',
} as const;

/**
 * Подписи и сообщения о неопубликованном (изменение черновика, черновик): только здесь есть «Отменить» (FR-058).
 * Опубликованное возвращают («Вернуть») или не вводят («Не вводить»).
 */
export const DRAFT = {
  cancelEdit: 'Отменить',
  revert: 'Отменить изменение',
  discard: 'Отменить черновик',
  discardTitle: 'Отменить черновик?',
  keep: 'Не отменять',
  discarded: 'Черновик отменён. Действуют прежние правила.',
  reverted: 'Изменение отменено.',
} as const;

/**
 * Действия с опубликованными версиями (US4; FR-046–FR-050): их возвращают («Вернуть») или не вводят («Не вводить»),
 * но не «отменяют». Единственное «отменён» здесь — о черновике (`draftOpen`): черновик не опубликован.
 */
export const VERSION = {
  rollback: 'Вернуть эти правила',
  rollbackDo: 'Вернуть правила',
  rollbackReason: 'Почему возвращаем',
  rollbackPlaceholder: 'Например: новые правила дали неверные часы у ночной смены.',
  rollbackGoldens: 'Возврат можно выполнить, даже если результат проверочного примера изменится.',
  rolledBack: 'Правила возвращены. Система пересчитывает табели.',
  whyRollback: 'Напишите, почему возвращаем.',
  implGone: 'Эти правила система больше не умеет считать. Вернуть их нельзя.',
  /** US4, сценарий 6: возврат недоступен сразу (`VersionView.rollback.reason = impl_gone`) — причина рядом с кнопкой. */
  rollbackImplGone:
    'Вернуть нельзя: в системе больше нет расчёта для одного из правил этой версии. Сообщите команде разработки.',
  impact: 'После возврата',
  notIntroduce: 'Не вводить эти правила',
  notIntroduceDo: 'Не вводить',
  notIntroduceReason: 'Почему не вводим',
  notIntroducePlaceholder: 'Например: заказчик решил оставить прежний обед.',
  notIntroduced: 'Эти правила не начнут действовать. Табели не изменились.',
  whyNotIntroduce: 'Напишите, почему не вводим.',
  /** FR-047, сценарий US4 5: при черновике кнопок нет, внизу истории — этот текст. */
  draftOpen: 'Вернуть или не вводить правила можно, когда черновик опубликован или отменён.',
} as const;

/** Статус версии на экране (data-model Г.3): опубликованное не «отменяют» — его возвращают или не вводят. */
export const VERSION_STATUS: Readonly<Record<VersionLabel, (validFrom: string) => string>> = {
  active: () => 'действует',
  past: () => 'действовала',
  future: (validFrom) => `начнёт действовать ${ruDate(validFrom)}`,
  replaced: () => 'заменена возвратом',
  not_introduced: () => 'не введена',
};

/** «Почему меняем» — вид основания версии (data-model В.1). */
export const BASIS_KIND_TEXT: Readonly<Record<string, string>> = {
  перенос: 'Начальные правила',
  ответ_на_вопрос: 'Ответ на вопрос реестра',
  решение_руководства: 'Решение руководства',
  приказ_распоряжение: 'Приказ или распоряжение',
  исправление_ошибки: 'Исправление ошибки в правиле',
  изменение_закона: 'Изменение закона',
  возврат: 'Возврат прежних правил',
  other: 'Другое основание',
};

/** Итог дня в примере, если часов нет или есть вопрос бригадиру. */
export const RESULT_TEXT = {
  noExit: 'нет выхода',
  noEntry: 'нет входа',
  onSite: 'на объекте',
  empty: 'проходов нет',
  over24: 'больше 24 ч',
  error: 'ошибка расчёта',
  question: 'вопрос бригадиру',
  /** Часы работы в выходной или сверхурочные без распоряжения: в ячейке, ячейка выделена (Q-13). */
  noOrder: 'ячейка выделена: нет распоряжения',
} as const;

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
const upperFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
/** Свободный текст как предложение: точка в конце, если её нет. */
const sentence = (s: string) => {
  const t = s.trim();
  return /[.!?…]$/u.test(t) ? t : `${t}.`;
};
const kindText = (kind: string) => BASIS_KIND_TEXT[kind] ?? BASIS_KIND_TEXT.other ?? '';
const hoursNum = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

/** Часы числом: «8», «8,25». */
export const hoursText = (h: number) => hoursNum.format(h);

const PLURAL = new Intl.PluralRules('ru-RU');
/** Слово по числу: `plural(3, ['день', 'дня', 'дней'])` → «дня». */
export function plural(n: number, [one, few, many]: readonly [string, string, string]): string {
  const c = PLURAL.select(n);
  return c === 'one' ? one : c === 'few' ? few : many;
}
const DAYS = ['день', 'дня', 'дней'] as const;
/** «в 1 дне», «в 3 днях», «в 21 дне» — без сокращения «дн.» (ревью T103, Н-10). */
const IN_DAYS = ['дне', 'днях', 'днях'] as const;
const PERSONS = ['человека', 'человек', 'человек'] as const;
const CHANGES = ['изменение', 'изменения', 'изменений'] as const;

/** Миллисекунды влияния — часами для показа: сервер даёт суммы в мс, интерфейс их только переводит в часы. */
const msHours = (ms: number) => hoursNum.format(ms / 3_600_000);

/** Сводка влияния, которую показывают строки «Это изменение» и «Весь черновик» (contracts/http-api.md, `Impact`). */
export interface ImpactNumbers {
  readonly changedDays: number;
  readonly people: number;
  readonly plusMs: number;
  readonly minusMs: number;
}

/** «вопрос реестра Q-25», «вопросы реестра Q-25, Q-26»; в начале предложения — «Вопросы реестра: Q-25, Q-26». */
function registry(codes: readonly string[], first = false): string {
  const words = codes.length === 1 ? 'вопрос реестра' : 'вопросы реестра';
  return first ? `${upperFirst(words)}: ${codes.join(', ')}` : `${words} ${codes.join(', ')}`;
}

/**
 * Основание: вид, дата, документ, что решили, вопросы реестра — для «Почему так» и документа для печати. Версию
 * возврата называет её номер, а не дата («правила от {дата}» читается двумя датами, ревью T103, В-5). `why` — строка
 * «Почему так»: у начальных правил — «так настроено при запуске системы» (Н-28; T106); вопросов реестра нет — ещё
 * «Решения заказчика по этому правилу нет».
 */
function basisText(b: BasisView, codes: readonly string[], why = false): string {
  if (b.kind === 'возврат') {
    const what =
      b.rollbackOf === undefined ? 'возврат прежних правил' : `возврат правил версии ${b.rollbackOf}`;
    const head = [what, b.date ? ruDate(b.date) : null].filter(Boolean).join(', ');
    return `${head}: ${sentence(b.decided)}`;
  }
  const initial = why && b.kind === 'перенос';
  const kind = initial ? 'так настроено при запуске системы' : lowerFirst(kindText(b.kind));
  const head = [kind, b.doc, b.date ? ruDate(b.date) : null].filter(Boolean).join(', ');
  const tail = codes.length
    ? ` ${registry(codes, true)}.`
    : initial
      ? ' Решения заказчика по этому правилу нет.'
      : '';
  return `${head}.${b.decided.trim() ? ` ${sentence(b.decided)}` : ''}${tail}`;
}

/** Тексты с подстановкой. Даты — `YYYY-MM-DD`, на экране — `дд.мм.гггг`. */
export const say = {
  versionActive: (validFrom: string, n: number) => `Действуют с ${ruDate(validFrom)}. Версия ${n}.`,
  /** 007, FR-004a: правила площадки до запуска — подсказка над правилами (Callout `accent`), начало жирным. */
  versionLaunch: (source: string, n: number) =>
    `Копия правил «${source}», версия ${n}. Начнут действовать при запуске площадки, опубликовать их здесь нельзя.`,
  launchSourceChanged: (source: string) =>
    `Правила «${source}» изменились после начала черновика. Новые правила образца в черновик не попали.`,
  versionOther: (date: string, n: number) =>
    `Правила на ${ruDate(date)}, версия ${n}. Сейчас действуют другие правила.`,
  versionInPeriod: (n: number, from: string) => `Версия ${n} · с ${ruDate(from)}`,
  found: (n: number) => `Найдено правил: ${n}`,
  notFound: (q: string) => `Правил со словом «${q}» нет.`,
  errorCode: (id: string) => `Код ошибки: ${id}`,
  /** «Почему так» (FR-005): основание последней версии, где правило изменилось, и вопросы реестра правила. */
  why: (b: BasisView | null, codes: readonly string[]) =>
    b ? `Почему так: ${basisText(b, codes, true)}` : `Почему так: ${registry(codes)}.`,
  /** «Почему так» правила закона — постоянная строка (`lawWhy`). */
  lawWhy: (text: string) => `Почему так: ${text}`,
  exampleWho: (name: string, date: string) => `${name}, ${ruDate(date)}`,
  hoursInCell: (h: number) => `в табеле ${hoursNum.format(h)} ч`,
  /** Раскладка ячейки по строкам, если строк с часами больше одной: «(Н 8, Я 1)» — видно, что делает правило. */
  cellRows: (rows: readonly { row: string; hours: number }[]) =>
    `(${rows.map((r) => `${r.row} ${hoursNum.format(r.hours)}`).join(', ')})`,
  passPair: (from: string, to: string) => `${from}–${to}`,
  passNoExit: (at: string) => `вход ${at}, выхода нет`,
  passNoEntry: (at: string) => `выход ${at}, входа нет`,
  section: (n: number, title: string, text: string) => `${n} · ${title} — ${text}`,
  more: (n: number) => `и ещё ${n}`,
  published: (date: string) => `Опубликовано ${ruDate(date)}`,
  validFrom: (date: string) => `Действует с ${ruDate(date)}`,
  author: (name: string) => `Опубликовано: ${name}`,
  basisKind: (kind: string) => `Почему меняем: ${lowerFirst(kindText(kind))}`,
  decided: (text: string) => `Что решили: ${text}`,
  doc: (doc: string) => `Документ: ${doc}`,
  /** «Вопрос реестра: Q-03», «Вопросы реестра: Q-25, Q-26». */
  question: (codes: readonly string[]) => registry(codes, true),
  /** Итог пересчёта (FR-041): «Пересчитано 120 дней. Изменились часы в 14 днях.» */
  recalcDone: (days: number, changed: number) =>
    `Пересчитано ${days} ${plural(days, DAYS)}. Изменились часы в ${changed} ${plural(changed, IN_DAYS)}.`,
  /** «3 дня не посчитаны.» */
  recalcFailed: (failed: number) =>
    `${failed} ${plural(failed, DAYS)} не ${plural(failed, ['посчитан', 'посчитаны', 'посчитаны'])}.`,
  /** Факт пересчёта разошёлся с влиянием (FR-040). */
  recalcMismatch: (n: number) =>
    `В ${n} ${plural(n, IN_DAYS)} часы после пересчёта не совпали с влиянием. Команда разработки получила сообщение.`,
  notIntroduced: (reason: string) => `Почему не вводим: ${reason}`,
  versionNumber: (n: number) => `Версия ${n}`,
  printVersion: (date: string, n: number, validFrom: string) =>
    `Правила на ${ruDate(date)}. Версия ${n}, действует с ${ruDate(validFrom)}.`,
  printBasis: (b: BasisView) => `Основание: ${basisText(b, b.questionCodes ?? [])}`,
  // US2: изменение правила, пример, влияние, черновик
  /** Последняя опубликованная версия ещё не действует (FR-019, US2 сценарий 2). */
  editFromFuture: (date: string) =>
    `Изменение строится от правил, которые начнут действовать ${ruDate(date)}.`,
  fixField: (label: string) => `Исправьте поле «${label}».`,
  numberRange: (min: number, max: number, unit = '') =>
    `Введите число от ${hoursNum.format(min)} до ${hoursNum.format(max)}${unit ? ` ${unit}` : ''}.`,
  numberMin: (min: number, unit = '') =>
    `Введите число не меньше ${hoursNum.format(min)}${unit ? ` ${unit}` : ''}.`,
  numberMax: (max: number, unit = '') =>
    `Введите число не больше ${hoursNum.format(max)}${unit ? ` ${unit}` : ''}.`,
  /** Что не сохранено — для окна «Сохранить в черновик?». */
  ruleSummary: (question: string) => `Правило «${question}»`,
  wasNow: (answer: string) => `${LABEL.wasNow} ${answer}`,
  /** Полоса черновика (FR-028). */
  draftMine: (n: number) => `Черновик ведёте вы. ${n} ${plural(n, CHANGES)}.`,
  draftOther: (name: string) => `Черновик ведёт ${name}. Вы можете смотреть, но не изменять.`,
  draftTransferred: (name: string) => `Черновик передан. Теперь его ведёт ${name}.`,
  discardText: (n: number) => `В черновике ${n} ${plural(n, CHANGES)}. Табели не изменятся.`,
  /** «Это изменение: 3 дня у 2 человек, +0,75 ч, −0,5 ч» (FR-023); дней нет — «часы не изменятся ни в одном дне». */
  impactMain: (label: string, s: ImpactNumbers) => {
    if (s.changedDays === 0) return `${label}: часы не изменятся ни в одном дне`;
    const hours = [
      s.plusMs > 0 ? `+${msHours(s.plusMs)} ч` : '',
      s.minusMs > 0 ? `−${msHours(s.minusMs)} ч` : '',
    ]
      .filter(Boolean)
      .map((h) => `, ${h}`)
      .join('');
    return `${label}: ${s.changedDays} ${plural(s.changedDays, DAYS)} у ${s.people} ${plural(s.people, PERSONS)}${hours}`;
  },
  impactQuestions: (plus: number, minus: number) => `вопросов бригадирам +${plus} −${minus}`,
  impactEdited: (n: number) => `дней с правкой, где изменится расчёт: ${n}`,
  /** Дни, которые новые правила не могут посчитать (не заполнено обязательное поле и т. п.), T106. */
  impactErrors: (n: number) => `дней с ошибкой расчёта: ${n}`,
  /** Правило меняет не часы дней (`RuleGroupView.effect`) — вместо «часы не изменятся ни в одном дне» (16.10.2026). */
  impactNotHours: (label: string, what: string) =>
    `${label}: часы дней не изменятся. Правило меняет ${what}.`,
  /** Варианты, которым нужен график работы, скрыты, пока на объекте нет графиков (решение 16.10.2026). */
  hiddenOptions: (n: number) =>
    `Ещё ${n} ${plural(n, ['вариант появится', 'варианта появятся', 'вариантов появятся'])}, когда на объекте будут графики работы.`,
  /** Дни, где после изменения часов не останется совсем, — предупреждение отдельной строкой (16.10.2026). */
  impactZeroed: (n: number) => `Внимание: в ${n} ${plural(n, IN_DAYS)} часов не останется совсем.`,
  signedDays: (n: number) => `Подписанные табели не изменятся: ${n} ${plural(n, DAYS)}.`,
  previewFrom: (date: string) => `Считаются дни с ${ruDate(date)}.`,
  daysFirst: (n: number) => `Показаны первые ${n}.`,
  // US3: публикация (FR-033–FR-037), новые версии (FR-062)
  whyDate: (earliest: string) => `Выберите дату не раньше ${ruDate(earliest)}.`,
  whyCalcErrors: (n: number) => `Расчёт по черновику даёт ошибку в ${n} ${plural(n, IN_DAYS)}.`,
  /** FR-035: дата начала внутри неподписанного табеля. */
  twoVersions: (name: string, date: string) =>
    `Табель «${name}» будет посчитан по двум версиям правил: до ${ruDate(date)} — по прежним, с ${ruDate(date)} — по новым.`,
  /** FR-035, сценарий 3: граница — будущая версия. */
  futureBoundary: (validFrom: string, publishedAt: string | null) =>
    `С ${ruDate(validFrom)} начнут действовать правила, опубликованные ${publishedAt ? ruDate(publishedAt) : 'раньше'}. Новая дата начала — не раньше ${ruDate(validFrom)}. Чтобы изменить правила раньше, сначала выберите «Не вводить эти правила» у версии с ${ruDate(validFrom)}.`,
  /** Подпись строки влияния: «С 01.10.2026». */
  impactFrom: (date: string) => `С ${ruDate(date)}`,
  goldenChange: (title: string, before: string, after: string) => `${title}: было ${before}, станет ${after}`,
  /** Пример из нескольких дней: только дни, которые меняются, с псевдонимом человека и датой. */
  goldenDetails: (title: string) => `${title}: изменятся пометки или другие подробности дня`,
  goldenChangeDays: (title: string, days: readonly { label: string; before: string; after: string }[]) =>
    `${title}: ${days.map((d) => `${d.label}: было ${d.before}, станет ${d.after}`).join('; ')}`,
  publishedFuture: (date: string) => `Опубликовано. Правила начнут действовать ${ruDate(date)}.`,
  newVersions: (n: number) => `Новые версии с вашего прошлого входа: ${n}.`,
  versionLink: (n: number, validFrom: string) => `Версия ${n} · с ${ruDate(validFrom)}`,
  // US4: возврат и «Не вводить» (FR-046–FR-050). Окна называют версию по номеру: «правила от {дата}» читалось то
  // датой начала, то датой публикации (ревью T103, В-5).
  rollbackTitle: (n: number) => `Вернуть правила версии ${n}?`,
  /** Сценарий US4 2: какие правила вернутся. */
  rollbackText: (n: number, validFrom: string) =>
    `Снова будут действовать правила версии ${n}, которые действовали с ${ruDate(validFrom)}. Последние изменения перестанут действовать.`,
  notIntroduceTitle: (n: number) => `Не вводить правила версии ${n}?`,
  notIntroduceText: (validFrom: string) =>
    `Эти правила не начнут действовать ${ruDate(validFrom)}. Табели не изменятся.`,
  goldenSame: (title: string) => `${title}: совпадает`,
  /** Версия возврата в истории: к какой версии вернули и почему. */
  rollbackOf: (n: number) => `Вернули правила версии ${n}.`,
  rollbackWhy: (text: string) => `Почему возвращаем: ${text}`,
  // US5: проверочные примеры
  /** «Закреплён: кто и когда»: «Админ Правил, 15.10.2026». */
  pinnedBy: (name: string, date: string) => `${name}, ${ruDate(date)}`,
  /** Пример по черновику (FR-052): «изменится: было 10 ч → станет 9 ч». */
  goldenWillChange: (before: string, after: string) => `изменится: было ${before} → станет ${after}`,
  goldenWillChangeDays: (days: readonly { label: string; before: string; after: string }[]) =>
    `изменится: ${days.map((d) => `${d.label}: было ${d.before} → станет ${d.after}`).join('; ')}`,
  // US6: просьбы о правилах (FR-057)
  /** Автор и дата просьбы: «Админ Правил, 16.10.2026». */
  requestBy: (name: string, date: string) => `${name}, ${ruDate(date)}`,
  inRegistry: (code: string) => `В реестре: ${code}`,
  rejected: (reason: string) => `Отклонена: ${reason}`,
};
